// Sprint CHIAVI-1 — elenco delle chiavi API dell'azienda (blocco aggiunto nella sezione dell'API pubblica)
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, KeyRound, Plus } from "lucide-react";
import { useAuth, isDeveloper } from "@/contexts/AuthContext";

const giorno = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { timeZone: "Europe/Rome" }) : null);
const err = (e) => e?.response?.data?.detail || "Operazione non riuscita";

export function RigaChiave({ k, scopi, onRevoca, onRinomina }) {
  const [nome, setNome] = useState(null);
  return (
    <tr className="border-t border-slate-100" data-testid={`chiave-${k.id}`}>
      <td className="py-2 pr-3">
        {nome === null ? <span data-testid={`chiave-nome-${k.id}`}>{k.nome}</span> : (
          <span className="flex gap-1"><Input className="h-8" value={nome} onChange={(e) => setNome(e.target.value)} data-testid={`chiave-nome-input-${k.id}`} />
            <Button size="sm" onClick={async () => { if (await onRinomina(k, nome)) setNome(null); }} data-testid={`chiave-nome-salva-${k.id}`}>Salva</Button></span>)}
      </td>
      <td className="py-2 pr-3" data-testid={`chiave-scopo-${k.id}`}>{scopi[k.scopo] || k.scopo || "—"}</td>
      <td className="py-2 pr-3 font-mono" data-testid={`chiave-last4-${k.id}`}>
        {k.last4 ? `…${k.last4}` : <span className="font-sans text-slate-400">non ricavabile (chiave preesistente)</span>}</td>
      <td className="py-2 pr-3" data-testid={`chiave-creata-${k.id}`}>
        {k.creata_il ? `${giorno(k.creata_il)}${k.creata_da ? ` · ${k.creata_da}` : ""}` : <span className="text-slate-400">non nota (chiave preesistente)</span>}</td>
      <td className="py-2 pr-3" data-testid={`chiave-uso-${k.id}`}>{giorno(k.ultimo_utilizzo) || <span className="text-slate-400">mai</span>}</td>
      <td className="py-2 pr-3" data-testid={`chiave-stato-${k.id}`}>
        {k.revocata_il ? <span className="text-rose-600">Revocata il {giorno(k.revocata_il)}</span> : <span className="text-emerald-700">Attiva</span>}</td>
      <td className="py-2 text-right whitespace-nowrap">
        {nome === null && <Button size="sm" variant="ghost" onClick={() => setNome(k.nome)} data-testid={`chiave-rinomina-${k.id}`}>Rinomina</Button>}
        {!k.revocata_il && <Button size="sm" variant="outline" className="text-rose-600 border-rose-200" onClick={() => onRevoca(k)}
          data-testid={`chiave-revoca-${k.id}`}>Revoca</Button>}
      </td>
    </tr>
  );
}

// Revoca: le chiavi di scopo «sito» chiedono la parola REVOCA scritta; le altre una conferma semplice
export function RevocaDialog({ k, onAnnulla, onConferma }) {
  const [parola, setParola] = useState("");
  const sito = k?.scopo === "sito";
  return (
    <Dialog open={!!k} onOpenChange={(o) => { if (!o) { setParola(""); onAnnulla(); } }}>
      <DialogContent className="max-w-md" data-testid="chiave-revoca-dialog">
        <DialogHeader><DialogTitle>Revocare «{k?.nome}»?</DialogTitle></DialogHeader>
        {sito ? <div className="space-y-2 text-sm">
          <p data-testid="chiave-revoca-avviso">Questa è una chiave del sito (per esempio quella usata da equocar.it). Revocandola il catalogo del sito si spegne
            subito e resterà spento finché non configurerai una chiave nuova sul sito.</p>
          <Label className="text-xs">Per confermare scrivi REVOCA</Label>
          <Input value={parola} onChange={(e) => setParola(e.target.value)} data-testid="chiave-revoca-parola" />
        </div> : <p className="text-sm">La chiave smetterà di funzionare subito. Le altre chiavi non cambiano.</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => { setParola(""); onAnnulla(); }} data-testid="chiave-revoca-annulla">Annulla</Button>
          <Button className="bg-rose-600 hover:bg-rose-700" disabled={sito && parola !== "REVOCA"}
            onClick={() => { setParola(""); onConferma(k); }} data-testid="chiave-revoca-conferma">Revoca</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NuovaChiave({ companyId, scopi, onCreata }) {
  const [aperta, setAperta] = useState(false);
  const [nome, setNome] = useState("");
  const [scopo, setScopo] = useState("sito");
  const [chiaro, setChiaro] = useState(null);
  const crea = async () => {
    try {
      const r = await api.post(`/companies/${companyId}/chiavi-api`, { nome, scopo });
      setChiaro(r.data.api_key); setNome(""); onCreata();
    } catch (e) { toast.error(err(e)); }
  };
  const chiudi = () => { setAperta(false); setChiaro(null); };
  return (<>
    <Button size="sm" onClick={() => setAperta(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="chiave-nuova-btn"><Plus className="h-4 w-4 mr-1" /> Nuova chiave</Button>
    <Dialog open={aperta} onOpenChange={(o) => { if (!o) chiudi(); }}>
      <DialogContent className="max-w-md" data-testid="chiave-nuova-dialog">
        <DialogHeader><DialogTitle>{chiaro ? "Chiave creata" : "Nuova chiave API"}</DialogTitle></DialogHeader>
        {chiaro ? <div className="space-y-2">
          <div className="text-xs text-amber-700 font-medium" data-testid="chiave-nuova-avviso">Copia la chiave adesso: non sarà più visibile.</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-sm font-mono bg-amber-50 px-2 py-1 rounded border border-amber-200 break-all" data-testid="chiave-nuova-valore">{chiaro}</code>
            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(chiaro); toast.success("Copiato"); }} data-testid="chiave-nuova-copia"><Copy className="h-4 w-4" /></Button>
          </div>
        </div> : <div className="space-y-3">
          <div><Label className="text-xs">Nome</Label><Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Sito equocar.it" data-testid="chiave-nuova-nome" /></div>
          <div><Label className="text-xs">Scopo</Label>
            <Select value={scopo} onValueChange={setScopo}><SelectTrigger data-testid="chiave-nuova-scopo"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(scopi).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
        </div>}
        <DialogFooter>{chiaro ? <Button onClick={chiudi} data-testid="chiave-nuova-chiudi">Fatto</Button>
          : <Button disabled={!nome.trim()} onClick={crea} data-testid="chiave-nuova-crea">Crea</Button>}</DialogFooter>
      </DialogContent>
    </Dialog>
  </>);
}

function Passaggio({ onFatto }) {
  const [piano, setPiano] = useState(null);
  const anteprima = () => api.get("/chiavi-api/passaggio/anteprima").then((r) => setPiano(r.data)).catch((e) => toast.error(err(e)));
  const conferma = async () => {
    try { const r = await api.post("/chiavi-api/passaggio/conferma"); setPiano(r.data); toast.success(`Copiate ${r.data.copiate.length} chiavi`); onFatto(); }
    catch (e) { toast.error(err(e)); }
  };
  return (
    <div className="mt-3 p-3 rounded-lg border border-slate-200 bg-slate-50 text-sm space-y-2" data-testid="chiavi-passaggio">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-slate-800">Passaggio delle chiavi preesistenti nell'elenco (tutte le aziende)</span>
        <Button size="sm" variant="outline" onClick={anteprima} data-testid="chiavi-passaggio-anteprima">Anteprima</Button>
      </div>
      {piano && (piano.aziende.length === 0 || piano.da_fare === 0
        ? <div className="text-slate-600" data-testid="chiavi-passaggio-niente">Niente da fare: nessuna chiave preesistente da copiare.</div>
        : <>
          <ul className="space-y-1">{piano.aziende.map((p) => (
            <li key={p.company_id} data-testid={`chiavi-passaggio-${p.company_id}`}>{p.azione === "copia"
              ? `Azienda ${p.azienda}: trovata 1 chiave, la copio nell'elenco come «${p.nome}», scopo «${p.scopo}», stesso valore, campo originale intatto.`
              : `Azienda ${p.azienda}: già nell'elenco, nessuna azione.`}</li>))}</ul>
          <Button size="sm" onClick={conferma} data-testid="chiavi-passaggio-conferma">Conferma il passaggio</Button>
        </>)}
    </div>
  );
}

export default function ChiaviApiSection({ companyId }) {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [revoca, setRevoca] = useState(null);
  const load = () => { if (companyId) api.get(`/companies/${companyId}/chiavi-api`).then((r) => setD(r.data)).catch(() => {}); };
  useEffect(load, [companyId]);
  const revocaChiave = async (k) => {
    try { await api.post(`/companies/${companyId}/chiavi-api/${k.id}/revoca`); toast.success("Chiave revocata"); setRevoca(null); load(); }
    catch (e) { toast.error(err(e)); }
  };
  const rinomina = async (k, nome) => {
    try { await api.put(`/companies/${companyId}/chiavi-api/${k.id}`, { nome }); load(); return true; }
    catch (e) { toast.error(err(e)); return false; }
  };
  if (!d) return null;
  return (
    <div className="mb-5" data-testid="chiavi-api-section">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-slate-500" /><h4 className="font-medium text-sm text-slate-800">Chiavi API</h4></div>
        <NuovaChiave companyId={companyId} scopi={d.scopi} onCreata={load} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 text-left"><tr><th className="pr-3">Nome</th><th className="pr-3">Scopo</th><th className="pr-3">Ultime 4</th>
            <th className="pr-3">Creata</th><th className="pr-3">Ultimo utilizzo</th><th className="pr-3">Stato</th><th /></tr></thead>
          <tbody>
            {d.chiavi.map((k) => <RigaChiave key={k.id} k={k} scopi={d.scopi} onRevoca={setRevoca} onRinomina={rinomina} />)}
            {!d.chiavi.length && <tr><td colSpan={7} className="py-3 text-slate-400" data-testid="chiavi-vuoto">Nessuna chiave nell'elenco</td></tr>}
          </tbody>
        </table>
      </div>
      {isDeveloper(user) && <Passaggio onFatto={load} />}
      <RevocaDialog k={revoca} onAnnulla={() => setRevoca(null)} onConferma={revocaChiave} />
    </div>
  );
}
